import axios, { AxiosRequestConfig, Method } from 'axios'
import { ConnectorBleDevice, ConnectorDeviceProfile, ConnectorHealth, ConnectorReleaseManifest, ConnectorSerialPort, MedconnectDevicesConfig, MedconnectDeviceValue } from '@/constants/medconnect-devices-types'

const MEDCONNECT_DEVICES_API_ROUTE = 'http://localhost:4001/api/main/'
const CONNECTOR_PROBE_TIMEOUT_MS = 5000
/** Where connectors that predate `GET version` (and its manifest pointer) publish their releases. */
const DEFAULT_RELEASE_MANIFEST_URL = 'https://medconnectinstallation.s3.eu-west-3.amazonaws.com/medconnect-devices/manifest.json'

/** Thrown by {@link probeConnectorHealth} when nothing answers on the connector port. */
export class ConnectorUnreachableError extends Error {
  constructor() {
    super('MedConnect devices connector unreachable')
    this.name = 'ConnectorUnreachableError'
  }
}

async function requestToConnector<T>(method: Method, route: string, data: unknown): Promise<T> {
  // axios request to api
  var options: AxiosRequestConfig = {
    method: method,
    headers: {
      'Content-Type': 'application/json',
    },
  }

  if (method.toUpperCase() === 'POST' || method.toUpperCase() === 'PUT' || method.toUpperCase() === 'PATCH') {
    options = {
      ...options,
      data: data,
    }
  }
  try {
    const resp = await axios(`${MEDCONNECT_DEVICES_API_ROUTE}${route}`, options)
    return resp.data
  } catch (error) {
    console.error(error)
    return {} as T
  }
}

export async function createDevice(device: MedconnectDeviceValue): Promise<MedconnectDevicesConfig> {
  return await requestToConnector<MedconnectDevicesConfig>('POST', 'device', device)
}

export async function updateDevice(deviceId: string, device: MedconnectDeviceValue): Promise<MedconnectDevicesConfig> {
  return await requestToConnector<MedconnectDevicesConfig>('PUT', `device/${deviceId}`, device)
}

export async function deleteDevice(deviceId: string): Promise<MedconnectDevicesConfig> {
  return await requestToConnector<MedconnectDevicesConfig>('DELETE', `device/${deviceId}`, {})
}

export async function restartDevice(deviceId: string): Promise<MedconnectDevicesConfig> {
  return await requestToConnector<MedconnectDevicesConfig>('GET', `device/${deviceId}/restart`, {})
}

export async function getConfig(): Promise<MedconnectDevicesConfig> {
  return await requestToConnector<MedconnectDevicesConfig>('GET', 'config', null)
}

/**
 * Health of the local connector, telling apart the three cases the UI must handle:
 * - resolves with the (normalised) report when the connector is current,
 * - resolves with `null` when a connector answers but predates `GET health`
 *   (legacy install: only config / device / restart routes exist),
 * - rejects with {@link ConnectorUnreachableError} when nothing answers.
 */
export async function probeConnectorHealth(): Promise<ConnectorHealth | null> {
  try {
    const resp = await axios.get<unknown>(`${MEDCONNECT_DEVICES_API_ROUTE}health`, { timeout: CONNECTOR_PROBE_TIMEOUT_MS })
    return normalizeConnectorHealth(resp.data)
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      if ([404, 405].includes(error.response.status)) return null
      throw error
    }
    if (await isLegacyConnectorAlive()) return null
    throw new ConnectorUnreachableError()
  }
}

/** Check the legacy config route when a missing health route is masked by a network / CORS error. */
async function isLegacyConnectorAlive(): Promise<boolean> {
  try {
    const response = await axios.get<Partial<MedconnectDevicesConfig>>(`${MEDCONNECT_DEVICES_API_ROUTE}config`, { timeout: CONNECTOR_PROBE_TIMEOUT_MS })
    return Array.isArray(response.data?.Devices)
  } catch (error) {
    return false
  }
}

/** Fill in the fields older (or partial) health reports leave out so consumers can rely on the type. */
export function normalizeConnectorHealth(raw: unknown): ConnectorHealth | null {
  if (!raw || typeof raw !== 'object') return null
  const report = raw as Partial<ConnectorHealth>
  if (report.status !== 'ok' && report.status !== 'degraded') return null
  return {
    status: report.status,
    uptimeSeconds: typeof report.uptimeSeconds === 'number' ? report.uptimeSeconds : 0,
    bluetooth: report.bluetooth && typeof report.bluetooth === 'object' ? report.bluetooth : null,
    devices: Array.isArray(report.devices) ? report.devices : [],
  }
}

export async function discoverBluetoothDevices(timeoutMs = 10000): Promise<{ devices: ConnectorBleDevice[] }> {
  const result = await requestToConnector<{ devices?: ConnectorBleDevice[] }>('GET', `bluetooth/discover?timeout=${timeoutMs}`, null)
  return { devices: Array.isArray(result?.devices) ? result.devices : [] }
}

export async function getDeviceProfiles(): Promise<ConnectorDeviceProfile[]> {
  const profiles = await requestToConnector<ConnectorDeviceProfile[]>('GET', 'device-profiles', null)
  return Array.isArray(profiles) ? profiles : []
}

export async function getSerialPorts(): Promise<ConnectorSerialPort[]> {
  const result = await requestToConnector<{ ports?: ConnectorSerialPort[] }>('GET', 'serial/ports', null)
  return Array.isArray(result.ports) ? result.ports : []
}

/** Installed version and manifest pointer; both `null` on a legacy connector that has no `GET version`. */
export async function getConnectorVersion(): Promise<{ version: string | null; releaseManifestUrl: string | null }> {
  const result = await requestToConnector<{ version?: string; releaseManifestUrl?: string }>('GET', 'version', null)
  return {
    version: typeof result?.version === 'string' ? result.version : null,
    releaseManifestUrl: typeof result?.releaseManifestUrl === 'string' ? result.releaseManifestUrl : null,
  }
}

/**
 * Latest published release next to the installed version. `currentVersion` is
 * `null` when the connector is too old to report it, which is itself the signal
 * that an update is due; the default manifest URL keeps the download link working.
 */
export async function getConnectorRelease(): Promise<{ currentVersion: string | null; manifest: ConnectorReleaseManifest }> {
  const current = await getConnectorVersion()
  const response = await axios.get<ConnectorReleaseManifest>(current.releaseManifestUrl ?? DEFAULT_RELEASE_MANIFEST_URL)
  return { currentVersion: current.version, manifest: response.data }
}

/**
 * Latest version and download for one OS. A platform's own `version` wins over the shared
 * top-level one, so a macOS-only release doesn't tell Windows users to "update" to the same MSI.
 */
export function selectPlatformRelease(manifest: ConnectorReleaseManifest, isMac: boolean): { latestVersion: string; downloadUrl?: string } {
  const platform = isMac ? manifest.macos : manifest.windows
  return { latestVersion: platform?.version ?? manifest.version, downloadUrl: platform?.url }
}

/** True when `candidate` is a strictly higher `major.minor.patch` than `installed` (a stale or older manifest is not an update). */
export function isNewerVersion(candidate: string, installed: string): boolean {
  const parse = (version: string): number[] => version.split(/[.+-]/).slice(0, 3).map((part) => parseInt(part, 10) || 0)
  const next = parse(candidate)
  const current = parse(installed)
  for (let i = 0; i < 3; i++) {
    if ((next[i] ?? 0) !== (current[i] ?? 0)) return (next[i] ?? 0) > (current[i] ?? 0)
  }
  return false
}

export type SchillerPatient = {
  id: string
  name: string
  lastname: string
  birthdate: string
  gender: string
  height: string
  weight: string
}

export enum ExaminationTypes {
  RestingECG = 'EKG01',
  StressECG = 'ERGO01',
  Spirometry = 'LUFU00',
  ErgoSpirometry = 'ERGO05',
  RestingRhythm = 'EKG02',
  LatePotentialECG = 'EKG03',
  HolterLongTermECG = 'EKG04',
  /** more to be added */
}
export type EcgDTO = {
  patient: SchillerPatient
  type?: ExaminationTypes
}

export async function launchECG(data: EcgDTO): Promise<boolean> {
  return await requestToConnector<boolean>('POST', 'ecg', data)
}

export type SpirometryDTO = {
  patient: SchillerPatient
  protocol?: 'HL7' | 'EXCHANGE'
}

export async function launchSpirometry(data: SpirometryDTO): Promise<{ success: boolean; message: string }> {
  return await requestToConnector<{ success: boolean; message: string }>('POST', 'spirometry', data)
}
